export const VALID_ROLES = [
  'admin',
  'district_admin',
  'district_coordinator',
  'school_admin',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'case_manager',
  'teacher',
  'teacher_full',
  'case_manager_full',
  'service_provider',
  'speech_case_manager',
  'school_psych',
  'paraeducator'
]

// Useful role groups for route guards
export const STUDENT_ACCESS_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'case_manager',
  'teacher',
  'teacher_full',
  'case_manager_full',
  'service_provider',
  'speech_case_manager',
  'school_psych',
  'paraeducator'
]

export const ADMIN_ONLY = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all'
]

/** Mirrors firestore.rules hasFullReadAccess() for student list/dev guard checks. */
export const FULL_READ_STUDENT_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'teacher_full',
  'case_manager_full',
  'school_psych'
]

export function hasFullStudentReadAccess(role) {
  return FULL_READ_STUDENT_ROLES.includes(role)
}

/** Admin-tier roles: skip provider-view narrowing in useStudentFilters. */
export const ROLES_WITHOUT_PROVIDER_VIEW_FILTER = [
  'admin',
  'district_admin',
  'school_admin',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'school_psych',
  'teacher_full',
  'case_manager_full'
]

// Who may *create* student feedback forms / staff surveys in the UI.
// sendBulkEmail role lists live in functions/email/index.js and are split
// by context (student-update vs feedback vs survey). Do not treat this
// array as the Gmail API allow-list.
export const FEEDBACK_CREATORS = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'case_manager',
  'case_manager_full',
  'speech_case_manager',
  'school_psych'
]

// District-tenancy: who may view the counts-only district dashboard. This is a
// DISTRICT-tier surface only — school `admin` and `school_admin` are scoped to
// their own school and intentionally do NOT get district access (nor the
// District Dashboard nav item / `/district` route). Empty of district roles in
// single-school projects → the nav item simply never appears for anyone.
export const DISTRICT_DASHBOARD_ROLES = ['district_admin', 'district_coordinator']

export const ADMIN_AND_CASE_MANAGERS = [
  'admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'case_manager',
  'case_manager_full',
  'speech_case_manager'
]

// School-level tools (gen-ed hours, extended access) are also available to
// district_admin acting on a school — these were reachable as /admin tabs but
// blocked as standalone routes, so route guards and menu visibility share this.
// district_coordinator is intentionally excluded (no student-data access).
export const ADMIN_CM_AND_DISTRICT_ADMIN = [...ADMIN_AND_CASE_MANAGERS, 'district_admin']

// Meeting Calendar (/meeting-calendar): IEP plan-review / reevaluation due-date
// overview. Open to SPED roles with a caseload or all-student read — admin_504
// is deliberately excluded (built for IEP compliance dates, not 504), while
// school_psych and the staff_* roles are included (full-read / coordination).
// district_admin mirrors admin (same admin tier); the nav item is school-scoped
// so it only surfaces in a school context.
export const MEETING_CALENDAR_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'sped_chair',
  'sped_chair_all',
  'case_manager',
  'case_manager_full',
  'speech_case_manager',
  'school_psych',
  'staff_view',
  'staff_edit',
  'staff_edit_docs'
]

// Caseload report (/caseload-report): printable per-case-manager roster with
// plan review / reevaluation dates and caseload counts. Built for SPED chairs;
// the admin tier mirrors its usual all-student read. Roles without full student
// read (plain case_manager etc.) are excluded — the page is a school-wide
// oversight tool, not a personal caseload view.
export const CASELOAD_REPORT_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'sped_chair',
  'sped_chair_all',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'school_psych'
]

// Gen-ed hours page: the roles above see every teacher's row (equity report).
// Teachers get the page too, but the view loads only their own row — mirrors
// firestore.rules genEdHours read: 'teacher' may read only genEdHours/{their
// uid}, while 'teacher_full' has full-collection read via hasFullReadAccess().
export const GEN_ED_HOURS_VIEWERS = [...ADMIN_CM_AND_DISTRICT_ADMIN, 'teacher', 'teacher_full']

/** Roles that appear in teacher/period dropdowns without `showInTeacherList`. */
export const TEACHER_DROPDOWN_ROLES = [
  'teacher',
  'teacher_full',
  'case_manager',
  'case_manager_full',
  'sped_chair',
  'sped_chair_all'
]

// --- Permission Actions ---
export const PERMISSION_ACTIONS = {
  VIEW_USERS: 'view_users',
  EDIT_USER: 'edit_user',
  DELETE_USER: 'delete_user',
  VIEW_STUDENTS: 'view_students',
  EDIT_STUDENT_CM: 'edit_student_CM', // Case Manager: edit students on their caseload
  EDIT_STUDENT_ALL: 'edit_student_All', // Admin/School Admin: edit any student
  ACCESS_ADMIN_PANEL: 'access_admin_panel', // Controls admin panel access
  MANAGE_AIDES: 'manage_aides', // Aide schedule management
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings', // System-level configuration
  MANAGE_TEACHER_FEEDBACK: 'manage_teacher_feedback', // Teacher feedback forms
  MANAGE_IMPORTS: 'manage_imports', // Data imports (SEIS, Aeries)
  MANAGE_BACKUPS: 'manage_backups' // Backup and restore operations
  // Note: Testing access is based on student access - no separate testing permissions needed
  // Removed: MANAGE_SUBJECTS, MANAGE_ROLES (unused in actual implementation)
}

// --- Permissions Matrix ---
// Coarse UI-level capability map mirroring firestore.rules at the action level.
// Rules are finer-grained (field scopes, plan gating, create-vs-update,
// governance policies) — canEditStudent() below and FULL_READ_STUDENT_ROLES are
// the closer mirrors for student data; when in doubt, firestore.rules wins.
export const PERMISSIONS_MATRIX = {
  admin: [
    // Full system access
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.DELETE_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_SYSTEM_SETTINGS,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK,
    PERMISSION_ACTIONS.MANAGE_IMPORTS,
    PERMISSION_ACTIONS.MANAGE_BACKUPS
  ],

  district_admin: [
    // All-school data access; district dashboard + user governance
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.DELETE_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_SYSTEM_SETTINGS,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK,
    PERMISSION_ACTIONS.MANAGE_IMPORTS,
    PERMISSION_ACTIONS.MANAGE_BACKUPS
  ],

  district_coordinator: [
    // District operations without student PII; may create users when policy allows
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_IMPORTS
  ],

  school_admin: [
    // School-level admin: Students + Aides + Users, NO system settings
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.DELETE_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK,
    PERMISSION_ACTIONS.MANAGE_IMPORTS,
    PERMISSION_ACTIONS.MANAGE_BACKUPS
    // NO MANAGE_SYSTEM_SETTINGS - cannot change system config
  ],

  staff_view: [
    // View-only access (renamed from administrator)
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS
  ],

  staff_edit: [
    // Edit all students, NO admin panel access
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL
    // NO ACCESS_ADMIN_PANEL - cannot access admin panel
  ],

  staff_edit_docs: [
    // Restricted edit: only additional documents and flags, NO admin panel
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL
  ],

  admin_504: [
    // 504 Coordinator: full peer of School Admin (also has a caseload).
    // Matches school_admin exactly: everything except MANAGE_SYSTEM_SETTINGS.
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.DELETE_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK,
    PERMISSION_ACTIONS.MANAGE_IMPORTS,
    PERMISSION_ACTIONS.MANAGE_BACKUPS
  ],

  sped_chair: [
    // Special education leadership with user management.
    // NO DELETE_USER — firestore.rules limit user deletes to the super-admin
    // tier (admin, district_admin, school_admin, admin_504).
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  sped_chair_all: [
    // Sped chair with IEP + 504 visibility, edits IEP only.
    // NO DELETE_USER — same rules constraint as sped_chair.
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.EDIT_USER,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_ALL,
    PERMISSION_ACTIONS.ACCESS_ADMIN_PANEL,
    PERMISSION_ACTIONS.MANAGE_AIDES,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  case_manager: [
    // Edit own caseload and manage teacher feedback
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_CM,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  teacher: [
    // View students in classes
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS
  ],

  teacher_full: [
    // View ALL students but behave like a teacher in class view
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS
  ],

  case_manager_full: [
    // View ALL students, edit own caseload, manage teacher feedback
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_CM,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  service_provider: [
    // View-only: service providers see served students but cannot edit them,
    // and have no caseload. (speech_case_manager is the service-provider role
    // that DOES have a caseload and edit rights.)
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS
  ],

  speech_case_manager: [
    // NEW: Same permissions as case managers
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.EDIT_STUDENT_CM,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  school_psych: [
    // View all students, create feedback forms, no editing
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS,
    PERMISSION_ACTIONS.MANAGE_TEACHER_FEEDBACK
  ],

  paraeducator: [
    // View assigned students only
    PERMISSION_ACTIONS.VIEW_USERS,
    PERMISSION_ACTIONS.VIEW_STUDENTS
  ]
}

/**
 * Determines whether a user can edit a student's data.
 * This can be moved to a composable like usePermissions.js if it becomes more complex.
 * @param {object} student - The student object from Firestore.
 * @param {object} currentUser - The currently logged-in user object.
 * @returns {boolean}
 */
export function canEditStudent(student, currentUser) {
  if (!currentUser || !student) return false

  const planCm = student.app?.studentData?.caseManagerId || student.caseManagerId || ''
  const evalCm = student.app?.evalCaseManagerId || ''
  const isOwnCase = planCm === currentUser.uid
  const isEvalCm = evalCm === currentUser.uid

  switch (currentUser.role) {
    case 'admin':
    case 'district_admin':
    case 'school_admin':
    case 'staff_edit':
    case 'staff_edit_docs':
    case 'admin_504':
      // 504 Coordinator is an admin-tier role (also acts as a case manager) and
      // can edit all students like school admins.
      return true
    case 'sped_chair':
    case 'sped_chair_all':
      // SPED Chair: Can only edit IEP students (not 504 students)
      return (student.app?.studentData?.plan || student.plan) === 'IEP'
    case 'case_manager':
    case 'case_manager_full':
    case 'speech_case_manager':
      // Own caseload, or the open-eval owner on a fillable shell (empty plan CM).
      return isOwnCase || isEvalCm
    case 'service_provider':
    case 'school_psych':
      // View-only roles: cannot edit students (no caseload)
      return false
    default:
      return false
  }
}

// Utility: is an approved role
export function isApprovedRole(role) {
  return VALID_ROLES.includes(role)
}

// Utility: can a role perform a given action?
export function can(role, action) {
  if (!role || !action) return false
  const allowed = PERMISSIONS_MATRIX[role]
  return !!(allowed && allowed.includes(action))
}

// Utility: can a user (object with .role) perform a given action?
export function canUser(user, action) {
  return user && can(user.role, action)
}

// --- Role Migration Map ---
// Use this to migrate existing users to new role structure
export const ROLE_MIGRATION_MAP = {
  // Old role -> New role
  administrator: 'staff_view', // General admin becomes view-only
  administrator_504_CM: 'admin_504', // 504 coordinator keeps similar access
  // All other roles stay the same
  admin: 'admin',
  sped_chair: 'sped_chair',
  case_manager: 'case_manager',
  teacher: 'teacher',
  service_provider: 'service_provider',
  paraeducator: 'paraeducator'
}

/**
 * Short tags for case-manager pickers. Full names stay in ROLE_DISPLAY_NAMES;
 * these exist so a crowded import dropdown can tell CM / CM SLP / CM Chair / 504
 * apart without reading the full role title.
 */
export const CM_ROLE_TAGS = {
  case_manager: 'CM',
  case_manager_full: 'CM',
  speech_case_manager: 'CM SLP',
  sped_chair: 'CM Chair',
  sped_chair_all: 'CM Chair',
  admin_504: '504'
}

export function cmRoleTag(role) {
  return CM_ROLE_TAGS[role] || ''
}

/** "Jane Davis (CM SLP)" for Add-to-App and other CM <select>s. */
export function cmDropdownLabel(user) {
  const name = (user?.name || user?.email || user?.id || 'Unknown').trim()
  const tag = cmRoleTag(user?.role)
  return tag ? `${name} (${tag})` : name
}

export const ROLE_DISPLAY_NAMES = {
  admin: 'Admin',
  district_admin: 'District Admin',
  district_coordinator: 'District Coordinator',
  school_admin: 'School Administrator',
  admin_504: '504 Coordinator',
  sped_chair: 'SPED Chair',
  sped_chair_all: 'SPED Chair (All)',
  case_manager: 'Case Manager',
  case_manager_full: 'Case Manager (All)',
  speech_case_manager: 'Speech Case Manager',
  staff_edit: 'Staff Edit (Full)',
  staff_edit_docs: 'Staff Edit (Docs Only)',
  service_provider: 'Service Provider',
  school_psych: 'School Psychologist',
  staff_view: 'Staff Viewer',
  teacher: 'Teacher',
  teacher_full: 'Teacher (All)',
  paraeducator: 'Paraeducator'
}

// --- Role Descriptions for UI ---
export const ROLE_DESCRIPTIONS = {
  admin: 'System Administrator - Full access to all features and settings',
  school_admin: 'School Administrator - Manage students, aides, and users (no system settings)',
  staff_view: 'Staff Viewer - IEP access for counselors, nurses, intervention specialists',
  staff_edit: 'Staff Edit (Full) - Edit all students, no admin panel access',
  staff_edit_docs:
    'Staff Edit (Docs Only) - Add documents and flags only, no other student data editing',
  admin_504: '504 Plan Coordinator - Manage 504 plans, students, and aides',
  sped_chair: 'Special Education Chair - Manage IEP students',
  sped_chair_all: 'SPED Chair (All) - View IEP and 504 students, edit IEP only',
  case_manager: 'Case Manager - Manage assigned student caseload only',
  teacher: 'Teacher - View students in assigned classes',
  teacher_full: 'Teacher (All) - View all students, class view shows assigned classes only',
  case_manager_full: 'Case Manager (All) - View all students, edit own caseload, manage feedback',
  service_provider: 'Service Provider - Manage students receiving services',
  speech_case_manager: 'Speech Case Manager - Provide speech services and manage caseload',
  school_psych: 'School Psychologist - View all students, create feedback forms, no editing',
  paraeducator: 'Paraeducator - View assigned students only'
}
